<template>
    <div class="card reservation-create-card position-relative">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                :title="pageTitle"
                @onClickCloseButton="goBack"
            />
        </div>

        <div class="px-2 mt-2">
            <div class="row">
                <div class="col-md-8 mb-2">
                    <div class="row">
                        <div class="col-md-3 mb-2">
                            <label class="form-label">Check-in</label>
                            <input v-model="formData.check_in" type="date" class="form-control" @change="computeCheckout"/>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-label">Nights</label>
                            <input v-model="formData.nights" type="number" min="1" class="form-control" @change="computeCheckout"/>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-label">Check-out</label>
                            <input v-model="formData.check_out" type="date" class="form-control" @change="onCheckoutChange"/>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-label">Room Type</label>
                            <v-select
                                placeholder="All room types"
                                v-model="selectedRoomType"
                                :options="roomTypes"
                                :reduce="type => type.id"
                                label="name"
                            />
                        </div>
                    </div>

                    <div class="d-flex flex-wrap gap-3 availability-legend mb-2">
                        <span><i class="legend-dot free"></i> Available</span>
                        <span><i class="legend-dot reserved"></i> Reserved</span>
                        <span><i class="legend-dot occupied"></i> Occupied</span>
                        <span><i class="legend-dot selected"></i> Selected</span>
                    </div>

                    <div class="availability-scroll" v-if="hasRooms">
                        <div v-for="group in groupedRooms" :key="group.id" class="room-group mb-3">
                            <div class="room-group-head">
                                <span class="room-group-name">{{ group.name }}</span>
                                <span class="room-group-count">{{ group.rooms.length }} rooms</span>
                            </div>

                            <div class="room-grid">
                                <div
                                    v-for="room in group.rooms"
                                    :key="room.id"
                                    class="room-card"
                                    :class="roomCardClass(room)"
                                    @click="onToggleRoom(room)"
                                >
                                    <div class="room-card-top">
                                        <span class="room-card-name">{{ room.name }}</span>
                                        <button
                                            v-if="isRoomSelected(room.id)"
                                            type="button"
                                            class="room-card-remove"
                                            title="Unselect room"
                                            @click.stop="removeRoomById(room.id)"
                                        >
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </div>
                                    <div class="room-card-status">{{ roomStatusLabel(room) }}</div>
                                    <div class="room-card-days">
                                        <span
                                            v-for="day in dayColumns"
                                            :key="day.key"
                                            class="day-pill"
                                            :class="cellClass(room, day)"
                                            :title="`${day.date} — ${cellTitle(room, day)}`"
                                        >{{ day.date }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-muted px-1">No rooms found for the selected criteria.</p>

                    <Pagination :offset="roomParams.offset" :state-name="'secondPaginationData'" @onPageChange="onRoomPageChange"/>
                </div>

                <div class="col-md-4 mb-2 reservation-form-col">
                    <form @submit.prevent="onSubmit">
                        <div class="mb-2">
                            <label class="form-label">Guest Name</label>
                            <div class="d-flex gap-1">
                                <AsyncSelect
                                    class="flex-grow-1"
                                    placeholder="Guest Name, Mobile No"
                                    v-model="selectedGuest"
                                    :api-service="fetchContactProfiles"
                                    :additional-query="{ type: 'customer' }"
                                    :format-label="formatPatientLabel"
                                />
                                <button type="button" class="btn btn-outline-primary" @click="openCustomerAddModal">
                                    <i class="fa fa-plus"></i>
                                </button>
                            </div>
                        </div>

                        <GuestModal
                            v-if="showGuestModal"
                            @onCreateProfile="onCreateGuest"
                            @onClose="closeCustomerAddModal"
                        />
                        <div class="mb-2">
                            <label class="form-label">Status</label>
                            <v-select
                                placeholder="Select status"
                                v-model="formData.registration_status"
                                :options="statusOptions"
                                :reduce="option => option.value"
                                label="label"
                            />
                        </div>

                        <div class="table-responsive mb-2" v-if="selectedRooms.length">
                            <table class="table table-sm table-bordered mb-0">
                                <thead>
                                    <tr>
                                        <th>Room</th>
                                        <th>Amount</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(room, index) in selectedRooms" :key="room.service_resource_id">
                                        <td>{{ room.name }}</td>
                                        <td>{{ roomAmount(room) }}</td>
                                        <td>
                                            <button type="button" class="btn btn-sm btn-outline-danger" @click="removeRoom(index)">x</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="mb-2">
                            <label class="form-label">Booked By</label>
                            <AsyncSelect
                                placeholder="Select staff"
                                v-model="selectedBookedBy"
                                :api-service="fetchContactProfiles"
                                :additional-query="{ type: 'employee' }"
                                :format-label="formatPatientLabel"
                            />
                        </div>
                        <div class="mb-2">
                            <label class="form-label">Referrer</label>
                            <AsyncSelect
                                placeholder="Select referrer"
                                v-model="selectedReferrer"
                                :api-service="fetchContactProfiles"
                                :additional-query="{ type: 'agent' }"
                                :format-label="formatPatientLabel"
                            />
                        </div>
                        <div class="mb-2">
                            <label class="form-label">Notes</label>
                            <textarea v-model="formData.notes" class="form-control" rows="2"></textarea>
                        </div>

                        <div class="d-flex justify-content-between align-items-center mt-2 mb-4">
                            <h4 class="mb-0">Total: {{ formData.total }}</h4>
                            <div>
                                <button
                                    v-if="! isEditMode"
                                    type="button"
                                    :disabled="loading"
                                    class="btn btn-outline-primary me-1"
                                    @click="onSubmit(true)"
                                >Reserve & Check-In</button>
                                <button type="submit" :disabled="loading" class="btn btn-primary">{{ submitLabel }}</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import GuestModal from '@/components/molecule/company/hotel/hotel-checkin/GuestModal.vue';
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';
import handleInventory from '@/services/modules/inventory';
import handleHospital from '@/services/modules/hospital';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';
import useDate from '@/services/utils/day';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchContactProfiles } = handleContact();
const { fetchHome } = handleInventory();
const { fetchGroup } = handleHospital();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { formatForInput, currentDate, tomorrowDate } = useDate();
const {
    storeHotelReservation,
    updateHotelReservation,
    fetchSingleHotelReservation,
    fetchHotelRoomRates,
    fetchHotelRoomAvailability,
} = handleHotel();

const reservationId = computed(() => {
    if (!$route.query.reservation_id) return null;
    return Number($route.query.reservation_id);
});
const isEditMode = computed(() => reservationId.value !== null);
const getPageTitle = () => {
    if (isEditMode.value) return 'Edit Reservation';
    return 'New Reservation';
};
const getSubmitLabel = () => {
    if (isEditMode.value) return 'Update Reserve';
    return 'Reserve';
};
const pageTitle = computed(getPageTitle);
const submitLabel = computed(getSubmitLabel);
const originalStatus = ref('');
const ownRoomIds = ref([]);

const loading = ref(false);
const roomTypes = ref([]);
const rooms = ref([]);
const statusOptions = ref([]);
const selectedRoomType = ref(null);
const selectedGuest = ref(null);
const selectedBookedBy = ref(null);
const selectedReferrer = ref(null);
const selectedRooms = ref([]);
const availabilityRequestId = ref(0);
const showGuestModal = ref(false);
const roomParams = reactive({
    offset: 12,
});
const formData = ref({
    registration_status: 'confirmed',
    check_in: currentDate(),
    check_out: tomorrowDate(),
    nights: 1,
    notes: '',
    total: 0,
});

const hasRooms = computed(() => rooms.value.length > 0);

const filteredRooms = computed(() => {
    if (!selectedRoomType.value) return rooms.value;
    return rooms.value.filter(room => String(room.group_id) === String(selectedRoomType.value));
});

const groupedRooms = computed(() => {
    const groups = [];
    const index = {};
    for (const room of filteredRooms.value) {
        const key = room.group_id ? room.group_id : 'ungrouped';
        if (!index[key]) {
            index[key] = { id: key, name: room.group_name ? room.group_name : 'Others', rooms: [] };
            groups.push(index[key]);
        }
        index[key].rooms.push(room);
    }
    return groups;
});

const resetRoomPage = () => {
    if (!$route.query.page) return;
    const routeQuery = Object.assign({}, $route.query);
    delete routeQuery.page;
    $router.replace({ path: $route.path, query: routeQuery });
};

watch(selectedRoomType, () => {
    resetRoomPage();
    getAvailability();
});

watch(reservationId, async () => {
    await loadReservation();
    await getAvailability();
});

const parseLocalDate = (value) => {
    const parts = String(value).split('-');
    if (parts.length !== 3) return new Date(value);
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

const dayColumns = computed(() => {
    const columns = [];
    if (!formData.value.check_in) return columns;
    const nights = Number(formData.value.nights) > 0 ? Number(formData.value.nights) : 1;
    const start = parseLocalDate(formData.value.check_in);
    for (let i = 0; i < nights; i++) {
        const day = new Date(start);
        day.setDate(start.getDate() + i);
        columns.push({
            key: formatForInput(day),
            date: day.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }),
        });
    }
    return columns;
});

const goBack = () => {
    $router.push({
        name: 'hotel-reservation',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: {
            reg_status: $route.query.reg_status,
            page: $route.query.page,
        },
    });
};

const openCustomerAddModal = () => {
    showGuestModal.value = true;
};

const closeCustomerAddModal = () => {
    showGuestModal.value = false;
};

const onCreateGuest = (profile) => {
    selectedGuest.value = { id: profile.id, name: profile.full_name };
    closeCustomerAddModal();
};

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const roomAmount = (room) => {
    return Number(room.rate) * Number(formData.value.nights);
};

const recalcTotal = () => {
    let total = 0;
    for (const room of selectedRooms.value) {
        total += Number(room.rate) * Number(formData.value.nights);
    }
    formData.value.total = total;
};

const computeCheckout = () => {
    if (!formData.value.check_in) return;
    if (!formData.value.nights) return;
    const checkIn = parseLocalDate(formData.value.check_in);
    checkIn.setDate(checkIn.getDate() + Number(formData.value.nights));
    formData.value.check_out = formatForInput(checkIn);
    afterRangeChange();
};

const onCheckoutChange = () => {
    if (!formData.value.check_in) return;
    if (!formData.value.check_out) return;
    const checkIn = parseLocalDate(formData.value.check_in);
    const checkOut = parseLocalDate(formData.value.check_out);
    const diff = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    if (diff <= 0) {
        formData.value.nights = 1;
        computeCheckout();
        return;
    }
    formData.value.nights = diff;
    afterRangeChange();
};

const afterRangeChange = () => {
    dropUnavailableSelectedRooms();
    recalcTotal();
    resetRoomPage();
    getAvailability();
};

const reservedStatuses = ['confirmed', 'pending'];

const bookingOn = (room, dayKey) => {
    if (!room.bookings) return null;
    const match = room.bookings.find(booking => {
        return dayKey >= booking.check_in && dayKey < booking.check_out;
    });
    if (!match) return null;
    return match;
};

const isBookedOn = (room, dayKey) => {
    return bookingOn(room, dayKey) !== null;
};

const isRoomAvailable = (room) => {
    if (ownRoomIds.value.includes(room.id)) return true;
    if (!dayColumns.value.length) return false;
    return dayColumns.value.every(day => !isBookedOn(room, day.key));
};

const isRoomSelected = (roomId) => {
    return selectedRooms.value.some(room => String(room.service_resource_id) === String(roomId));
};

const bookingKind = (booking) => {
    if (reservedStatuses.includes(booking.registration_status)) return 'reserved';
    return 'occupied';
};

const cellClass = (room, day) => {
    const booking = bookingOn(room, day.key);
    if (booking) return `pill-${bookingKind(booking)}`;
    if (isRoomSelected(room.id)) return 'pill-selected';
    return 'pill-free';
};

const cellTitle = (room, day) => {
    const booking = bookingOn(room, day.key);
    if (!booking) return 'Available';
    return `${bookingKind(booking) === 'reserved' ? 'Reserved' : 'Occupied'} (${booking.reservation_no})`;
};

const roomStatusLabel = (room) => {
    if (isRoomSelected(room.id)) return 'Selected';
    if (isRoomAvailable(room)) return 'Available';
    const kinds = new Set();
    for (const day of dayColumns.value) {
        const booking = bookingOn(room, day.key);
        if (booking) kinds.add(bookingKind(booking));
    }
    if (kinds.has('occupied')) return 'Occupied';
    return 'Reserved';
};

const roomCardClass = (room) => {
    if (isRoomSelected(room.id)) return 'is-selected';
    if (!isRoomAvailable(room)) return 'is-booked';
    return 'is-free';
};

const onToggleRoom = (room) => {
    if (isRoomSelected(room.id)) {
        removeRoomById(room.id);
        return;
    }
    if (!isRoomAvailable(room)) {
        showError(`${room.name} is already booked in the selected date range.`);
        return;
    }
    addRoom(room);
};

const addRoom = async (room) => {
    const added = {
        service_resource_id: room.id,
        name: room.name,
        rate: room.rate ? room.rate : 0,
    };
    selectedRooms.value.push(added);
    if (!added.rate) await applyRoomRate(added);
    recalcTotal();
};

const applyRoomRate = async (room) => {
    const query = `${getBaseQuery()}&room_ids[]=${room.service_resource_id}`;
    await fetchHotelRoomRates(query).then(res => {
        if (!res.status) {
            showError(res.message);
            removeRoomById(room.service_resource_id);
            return;
        }
        if (!res.data.length) return;
        const match = res.data.find(item => String(item.service_resource_id) === String(room.service_resource_id));
        if (!match) return;
        room.rate = match.rate;
    });
};

const removeRoom = (index) => {
    selectedRooms.value.splice(index, 1);
    recalcTotal();
};

const removeRoomById = (roomId) => {
    const index = selectedRooms.value.findIndex(room => String(room.service_resource_id) === String(roomId));
    if (index < 0) return;
    removeRoom(index);
};

const dropUnavailableSelectedRooms = () => {
    selectedRooms.value = selectedRooms.value.filter(selected => {
        const room = rooms.value.find(item => String(item.id) === String(selected.service_resource_id));
        if (!room) return true;
        return isRoomAvailable(room);
    });
    recalcTotal();
};

const getStatusOptions = async () => {
    await fetchHome().then(({ data }) => {
        if (!data) return;
        if (!data.hotel_reservation_statuses) return;
        const allowedStatuses = ['confirmed', 'pending'];
        statusOptions.value = data.hotel_reservation_statuses.filter(status => {
            return allowedStatuses.includes(status.value);
        }).map(status => {
            return { label: status.name, value: status.value };
        });
    });
};

const getRoomTypes = async () => {
    await fetchGroup($route.params.companyId, 'places').then(res => {
        if (res.status) roomTypes.value = res.data;
    });
};

const buildAvailabilityQuery = () => {
    let query = getBaseQuery();
    if (formData.value.check_in) query += `&check_in=${formData.value.check_in}`;
    if (formData.value.check_out) query += `&check_out=${formData.value.check_out}`;
    if (selectedRoomType.value) query += `&room_type_id=${selectedRoomType.value}`;
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + roomParams.offset;
    return query;
};

const setRoomPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        stateName: 'secondPaginationData',
        data: {
            records: data.total,
            perPage: data.per_page,
            currentPage: data.current_page,
        },
    });
};

const getAvailability = async () => {
    availabilityRequestId.value += 1;
    const requestId = availabilityRequestId.value;
    loading.value = true;
    await fetchHotelRoomAvailability(buildAvailabilityQuery()).then(res => {
        if (requestId !== availabilityRequestId.value) return;
        if (!res.status) {
            rooms.value = [];
            $store.commit('resetPagination', 'secondPaginationData');
            dropUnavailableSelectedRooms();
            return;
        }
        rooms.value = res.data.data;
        setRoomPagination(res.data);
        dropUnavailableSelectedRooms();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        if (requestId === availabilityRequestId.value) loading.value = false;
    });
};

const validate = () => {
    if (!selectedGuest.value) {
        showError('Please select a guest');
        return false;
    }
    if (!formData.value.check_in) {
        showError('Please select check-in date');
        return false;
    }
    if (!formData.value.check_out) {
        showError('Please select check-out date');
        return false;
    }
    if (new Date(formData.value.check_out) <= new Date(formData.value.check_in)) {
        showError('Check-out date must be after check-in date');
        return false;
    }
    if (!selectedRooms.value.length) {
        showError('Please select at least one room');
        return false;
    }
    const zeroRate = selectedRooms.value.find(room => Number(room.rate) <= 0);
    if (zeroRate) {
        showError(`Please set a rate for ${zeroRate.name}`);
        return false;
    }
    return true;
};

const buildPayload = () => {
    return {
        company_id: $route.params.companyId,
        contact_profile_id: selectedGuest.value ? selectedGuest.value.id : null,
        admitted_by: selectedBookedBy.value ? selectedBookedBy.value.id : null,
        referrer_id: selectedReferrer.value ? selectedReferrer.value.id : null,
        registration_status: formData.value.registration_status,
        check_in: formData.value.check_in,
        check_out: formData.value.check_out,
        nights: formData.value.nights,
        rooms: selectedRooms.value,
        notes: formData.value.notes,
    };
};

const submitReservation = (payload) => {
    if (isEditMode.value) return updateHotelReservation(payload, reservationId.value);
    return storeHotelReservation(payload);
};

const goToCheckin = (ipdRegisterId) => {
    $router.push({
        name: 'hotel-checkin',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
            ipdRegisterId,
        },
    });
};

const onSubmit = async (checkinAfter) => {
    if (!validate()) return;
    loading.value = true;
    await submitReservation(buildPayload()).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        if (checkinAfter === true) {
            goToCheckin(res.data.id);
            return;
        }
        goBack();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const resetForEditMode = () => {
    selectedRooms.value = [];
    ownRoomIds.value = [];
    originalStatus.value = '';
};

const loadReservation = async () => {
    resetForEditMode();
    if (!isEditMode.value) return;
    await fetchSingleHotelReservation(getBaseQuery(), reservationId.value).then(res => {
        if (!res.status) return;
        const data = res.data;
        formData.value.registration_status = data.registration_status;
        originalStatus.value = data.registration_status;
        formData.value.check_in = data.check_in;
        formData.value.check_out = data.check_out;
        formData.value.nights = data.nights;
        formData.value.notes = data.notes;
        selectedGuest.value = data.guest;
        selectedBookedBy.value = data.booked_by;
        selectedReferrer.value = data.referrer;
        selectedRooms.value = data.rooms.map(room => {
            return {
                service_resource_id: room.service_resource_id,
                name: room.name,
                rate: room.rate,
            };
        });
        ownRoomIds.value = data.rooms.map(room => room.service_resource_id);
        recalcTotal();
    });
};

const onRoomPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query);
    routeQuery.page = page;
    await $router.replace({ path: $route.path, query: routeQuery });
    await getAvailability();
};

onMounted(async () => {
    loading.value = true;
    await Promise.all([
        getStatusOptions(),
        getRoomTypes(),
        loadReservation(),
    ]).finally(async () => {
        await getAvailability();
        loading.value = false;
    });
});
</script>

<style scoped>
.reservation-create-card {
    min-height: 75vh;
    padding-bottom: 2rem;
}
.availability-legend span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: #4a5568;
}
.legend-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
}
.legend-dot.free {
    background: #22c55e;
}
.legend-dot.reserved {
    background: #f59e0b;
}
.legend-dot.occupied {
    background: #ef4444;
}
.legend-dot.selected {
    background: #3b82f6;
}
.room-group-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.6rem;
}
.room-group-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1f2937;
}
.room-group-count {
    font-size: 0.72rem;
    color: #6b7280;
    background: #f1f5f9;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
}
.availability-scroll {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 0.25rem;
}
.reservation-form-col {
    border-left: 1px solid #eef0f3;
}
.room-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
}
.room-card {
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    padding: 0.75rem;
    cursor: pointer;
    background: #fff;
    transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}
.room-card:hover {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
}
.room-card.is-booked {
    background: #fef2f2;
    border-color: #fecaca;
}
.room-card.is-selected {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
    background: #eff6ff;
}
.room-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.room-card-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #111827;
}
.room-card-remove {
    border: none;
    background: #3b82f6;
    color: #fff;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    line-height: 1;
    padding: 0;
    cursor: pointer;
}
.room-card-remove:hover {
    background: #2563eb;
}
.room-card-status {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-top: 0.15rem;
    color: #6b7280;
}
.room-card.is-selected .room-card-status {
    color: #2563eb;
}
.room-card.is-booked .room-card-status {
    color: #dc2626;
}
.room-card-days {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.6rem;
}
.day-pill {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.12rem 0.4rem;
    border-radius: 6px;
    white-space: nowrap;
    border: 1px solid transparent;
}
.pill-free {
    background: #ecfdf5;
    color: #15803d;
    border-color: #bbf7d0;
}
.pill-reserved {
    background: #fffbeb;
    color: #b45309;
    border-color: #fde68a;
}
.pill-occupied {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fecaca;
}
.pill-selected {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
}
</style>
